import { Injectable } from '@angular/core';
import { isTauri } from './tauri-http';

/**
 * SSO côté desktop (app Tauri).
 *
 * La webview Tauri bloque les popups de Firebase Auth (`auth/popup-blocked`),
 * et son origine (`tauri://localhost` / `http://tauri.localhost`) n'est de toute
 * façon pas une origine HTTPS prévisible pour les fournisseurs OAuth.
 *
 * Flux mis en place :
 *  1. L'utilisateur clique sur un bouton SSO dans l'app.
 *  2. On ouvre une page "passerelle" (bridge) de la web app, hébergée sur un
 *     vrai domaine HTTPS, dans le **navigateur système** (`@tauri-apps/plugin-opener`).
 *  3. Cette page exécute `signInWithPopup` Firebase (OK depuis une vraie origine
 *     HTTPS), récupère le `_tokenResponse` (idToken Firebase + email/noms), puis
 *     redirige le navigateur vers `narratheque://sso?state=<s>&payload=<base64>`.
 *  4. Le schéma custom `narratheque://` rouvre l'app Tauri ; le plugin deep-link
 *     nous remet l'URL (`onOpenURL` / `getCurrent`). On vérifie le `state`
 *     (anti-CSRF), on décode le payload et on restitue un objet "user"
 *     compatible avec `AuthService.loginWithSSO` (qui lit `user._tokenResponse`).
 *
 * Aucune modification back pour cette étape : l'idToken Firebase est ensuite
 * consommé tel quel par `POST /auth/register_sso`.
 */
export type SsoProvider = 'google' | 'facebook' | 'apple';

export interface SsoResult {
  /** Objet synthétique compatible `user._tokenResponse` (loginWithSSO). */
  user: { _tokenResponse: Record<string, any> };
}

interface PendingRequest {
  state: string;
  resolve: (r: SsoResult) => void;
  reject: (e: Error) => void;
  /** Timeout de sécurité : si l'utilisateur ne revient jamais. */
  timer: any;
}

@Injectable({ providedIn: 'root' })
export class DesktopSsoService {
  private static readonly SCHEME_PATH = 'sso';
  /** Délai avant abandon (3 min). */
  private static readonly TIMEOUT_MS = 3 * 60 * 1000;

  private listenerPromise: Promise<void> | null = null;
  private pending: PendingRequest | null = null;
  /** Dernier payload reçu sans requête en attente (cold start). */
  private buffered: { state: string; result: SsoResult } | null = null;

  /**
   * À appeler une fois au boot (depuis AppComponent) en mode desktop :
   * enregistre l'écouteur deep-link et consomme l'URL qui aurait lancé l'app.
   */
  async init(): Promise<void> {
    if (!isTauri() || this.listenerPromise) return;
    this.listenerPromise = (async () => {
      const { onOpenUrl, getCurrent } = await import(
        '@tauri-apps/plugin-deep-link'
      );

      console.log('[DesktopSso] init: enregistrement du listener deep-link');

      onOpenUrl((urls) => {
        console.log('[DesktopSso] onOpenUrl reçu:', urls);
        (urls ?? []).forEach((u) => this.handleUrl(u));
      }).catch((e) => {
        console.warn('[DesktopSso] onOpenUrl erreur:', e);
      });

      try {
        const initial = await getCurrent();
        console.log('[DesktopSso] getCurrent:', initial);
        (initial ?? []).forEach((u) => this.handleUrl(u));
      } catch (e) {
        console.warn('[DesktopSso] getCurrent erreur:', e);
      }

      // Chemin robuste sur Linux : le callback single-instance (Rust) extrait
      // l'URL de l'argv et l'émet comme event custom (le forwarding interne du
      // plugin deep-link étant peu fiable sur Linux).
      try {
        const { listen } = await import('@tauri-apps/api/event');
        listen<string>('sso-deep-link', (event) => {
          console.log('[DesktopSso] event sso-deep-link:', event.payload);
          if (event.payload) this.handleUrl(event.payload);
        }).catch((e) => console.warn('[DesktopSso] listen erreur:', e));
      } catch (e) {
        console.warn('[DesktopSso] import @tauri-apps/api/event erreur:', e);
      }
    })();
  }

  /**
   * Lance l'auth SSO dans le navigateur système et résout à son retour.
   * Ne doit être appelée QUE si `isTauri()` est vrai.
   */
  async signInWithProvider(provider: SsoProvider): Promise<SsoResult> {
    const state = this.randomState();
    const bridgeUrl = this.buildBridgeUrl(provider, state);

    // Si un payload correspondant au state était déjà arrivé (cold start),
    // on le consomme directement.
    if (this.buffered && this.buffered.state === state) {
      const r = this.buffered.result;
      this.buffered = null;
      return r;
    }

    const { openUrl } = await import('@tauri-apps/plugin-opener');
    await openUrl(bridgeUrl);

    return new Promise<SsoResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending?.state === state) {
          this.pending = null;
          reject(
            new Error(
              "La connexion SSO n'est pas revenue à temps. Réessayez.",
            ),
          );
        }
      }, DesktopSsoService.TIMEOUT_MS);

      this.pending = { state, resolve, reject, timer };
    });
  }

  // --- Internes -------------------------------------------------------------

  private buildBridgeUrl(provider: SsoProvider, state: string): string {
    const env = (window as any).__env ?? {};
    const base: string =
      env.webAppUrl || env.webAppUrlFallback || 'https://app.narratheque.io';
    const path = `/desktop-sso?provider=${encodeURIComponent(
      provider,
    )}&state=${encodeURIComponent(state)}`;
    return base.replace(/\/$/, '') + path;
  }

  private async handleUrl(raw: string): Promise<void> {
    console.log('[DesktopSso] handleUrl:', raw, '| pending:', !!this.pending);
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      console.warn('[DesktopSso] handleUrl: URL non parsable');
      return;
    }
    // On accepte `narratheque://sso?...` (le host peut être `sso`).
    if (url.host !== DesktopSsoService.SCHEME_PATH) {
      console.warn('[DesktopSso] handleUrl: host ignoré =', url.host);
      return;
    }

    const params = url.searchParams;
    const state = params.get('state') ?? '';

    const error = params.get('error');
    if (error) {
      if (this.pending && this.pending.state === state) {
        clearTimeout(this.pending.timer);
        const reject = this.pending.reject;
        this.pending = null;
        reject(new Error(decodeURIComponent(error)));
      }
      return;
    }

    const payload = params.get('payload');
    if (!payload) return;

    let tokenResponse: Record<string, any>;
    try {
      tokenResponse = JSON.parse(this.base64Decode(payload));
    } catch {
      return;
    }

    const result: SsoResult = { user: { _tokenResponse: tokenResponse } };

    if (this.pending && this.pending.state === state) {
      clearTimeout(this.pending.timer);
      const resolve = this.pending.resolve;
      this.pending = null;
      resolve(result);
    } else {
      // Reçu avant l'attente (cold start) : on bufferise brièvement.
      this.buffered = { state, result };
    }
  }

  private randomState(): string {
    const arr = new Uint8Array(16);
    (crypto || (window as any).msCrypto).getRandomValues(arr);
    return Array.from(arr)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /** base64url → string (compatible pont navigateur). */
  private base64Decode(b64url: string): string {
    const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
    const binary = atob(b64 + pad);
    try {
      // UTF-8 safe.
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    } catch {
      return binary;
    }
  }
}
